import axios from 'axios';
import vopayApi from '@/services/api.config';
import { useClientData } from '@/providers/ClientData.provider';
import { SupportedCurrencies } from 'vopay';
import { useEffect, useState } from 'react';
import { useDataFetchIndicator } from '../';

const { api } = vopayApi;

export type Balance = {
  AccountBalance: number;
  AvailableFunds: number;
  Currency: SupportedCurrencies;
  PendingFunds: number;
  Reserve: number;
  RollingReserveNSF: number;
  RollingReserveReturns: number;
  RollupAvailableBalance: number | null;
};

export default function useAccountBalances() {
  const source = axios.CancelToken.source();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [, setPending] = useDataFetchIndicator();

  const {
    response: { AccountID },
  } = useClientData();

  useEffect(() => {
    fetchBalances();

    return () => source.cancel();
  }, []);

  async function fetchBalances() {
    try {
      setPending(true);

      const promises = ['CAD', 'USD'].map((currency) => {
        return api.postAccountBalance(
          {
            PortalUser: AccountID,
            Currency: currency,
          },
          source,
        );
      });

      const responses = await Promise.all(promises);

      const error = responses.find((response) => !response.Success);

      if (error) {
        throw new Error(error.ErrorMessage);
      }

      const parsedBalances = responses.map((response) => ({
        AccountBalance: parseFloat(response.AccountBalance),
        AvailableFunds: parseFloat(response.AvailableFunds),
        Currency: response.Currency,
        PendingFunds: parseFloat(response.PendingFunds),
        Reserve: parseFloat(response.Reserve),
        RollingReserveNSF: parseFloat(response.RollingReserveNSF),
        RollingReserveReturns: parseFloat(response.RollingReserveReturns),
        RollupAvailableBalance: response.RollupAvailableBalance ? parseFloat(response.RollupAvailableBalance) : null,
      }));

      setBalances(parsedBalances);
    } catch (err) {
      console.error(err);

      if (err instanceof Error) throw err;

      throw new Error('An error occurred while fetching account balances');
    } finally {
      setPending(false);
    }
  }

  return {
    balances,
  };
}
